import { Table, type TableProps } from 'antd';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useCrmLocale } from '../../ui/locale/crmLocale';
import { useScrollableFocus } from '../reception/useScrollableFocus';
import { CrmEmptyState, CrmErrorState } from './CrmFeedbackState';
import './CrmTable.css';

export type CrmTableProps<T extends object> = Omit<TableProps<T>, 'rowKey'> & {
  rowKey: NonNullable<TableProps<T>['rowKey']>;
  error?: ReactNode;
  onRetry?: () => void;
  emptyTitle?: ReactNode;
  ariaLabel: string;
};

/** Presentation only: callers own data, sorting, filters and pagination contracts. */
export function CrmTable<T extends object>({ error, onRetry, emptyTitle, ariaLabel, locale,
  loading, pagination = false, size = 'small', scroll, className = '', ...props }: CrmTableProps<T>) {
  const { t } = useCrmLocale();
  const busy = Boolean(loading && (typeof loading === 'boolean' || loading.spinning !== false));
  return <section className={`crm-table ${className}`} aria-label={ariaLabel} aria-busy={busy}>
    {error ? <CrmErrorState size="compact" title={error} onAction={onRetry} actionLabel={t('Retry')} />
      : <Table<T> {...props} size={size} loading={loading} pagination={pagination}
        scroll={{ x: 'max-content', ...scroll }} locale={{
          emptyText: busy ? null : <CrmEmptyState size="compact" title={emptyTitle ?? t('No data available')} />,
          ...locale,
        }} />}
  </section>;
}

export type CrmTableSurfaceProps = ComponentPropsWithoutRef<'div'> & { 'aria-label': string };

/** Shared styling/keyboard scrolling for native tables with specialized markup. */
export function CrmTableSurface({ className = '', children, ...props }: CrmTableSurfaceProps) {
  const focus = useScrollableFocus<HTMLDivElement>();
  return <div {...props} ref={focus.ref} role="region" tabIndex={focus.tabIndex}
    className={`crm-table-surface ${className}`}>{children}</div>;
}
